/* Labelling episodes, in words: where an episode's labels stand, how much of the work a
 * person does, and which machine reads.
 *
 * The tiers are the one price table, `packages/sx-contracts/sx_contracts/labelling_prices.json`,
 * which the Catalog's quote reads as `sx_contracts.labelling`; this module imports its rendering
 * (`labelling-prices.generated.json`, `tools/render_console_tables.py`), so the console offers
 * exactly the tiers the quote prices. Edit the table there.
 * Only enum types are imported: their runtime lives in the generated-RPC chunk, and the
 * Library's search parser must not pull it into first paint. */

import type {
  AnnotationGrade,
  EpisodeLabelState,
  LabelProduct,
} from "@sx/protocol/experience/catalog/annotations";
import type { FailureReason } from "@sx/protocol/operations";
import type { StatusTone } from "@sx/ui";

import rendered from "./labelling-prices.generated.json";

const table = rendered.table;

export interface LabelStateWords {
  readonly label: string;
  readonly tone: StatusTone;
}

/** Where an episode's labels stand. Only a machine reading it now is still moving, so only
 * that state pulses; a finished reading reads as ink. */
export const LABEL_STATE_WORDS: Readonly<Record<EpisodeLabelState, LabelStateWords>> = {
  0: { label: "Unknown", tone: "idle" },
  1: { label: "Not labelled", tone: "idle" },
  2: { label: "Machine labelled", tone: "ok" },
  3: { label: "With a person", tone: "warn" },
  4: { label: "Reviewed", tone: "ok" },
  5: { label: "Labelling", tone: "live" },
};

/** Why a machine could not label an episode: its latest labelling Job failed for a reason
 * the episode's own bytes decide, so asking again would not help. */
export const UNREADABLE_WORDS: Readonly<Record<Exclude<FailureReason, 0>, string>> = {
  1: "Too long to read",
  2: "A camera is missing",
  3: "The recording has no index",
  4: "Not a kind the labeller reads",
};

/** Where an episode's labels stand, said with why a machine could not label it when that
 * is why it is not labelled: the reason is the state word, in the failed colour. */
export function labelStateWords(
  state: EpisodeLabelState,
  unreadable: FailureReason | undefined,
): LabelStateWords {
  if (unreadable === undefined || unreadable === 0) return LABEL_STATE_WORDS[state];
  return { label: UNREADABLE_WORDS[unreadable], tone: "error" };
}

/** A machine is reading the episode now; the one state that changes on its own. */
export const LABELLING: EpisodeLabelState = 5;

/** Whether any listed episode is still being labelled, so the list keeps asking until its
 * reading lands and the row says what the machine found. */
export function anyStillLabelling(
  pages: readonly { readonly episodes: readonly { readonly labelState: EpisodeLabelState }[] }[],
): boolean {
  return pages.some((page) => page.episodes.some((episode) => episode.labelState === LABELLING));
}

/** Every label state but unspecified, in the order the work goes. */
export const LABEL_STATES: readonly EpisodeLabelState[] = [1, 5, 2, 3, 4];

export interface GradeWords {
  readonly label: string;
  readonly description: string;
}

/** How much of the labelling a person does, as a person chooses it. */
export const GRADE_WORDS: Readonly<Record<Exclude<AnnotationGrade, 0>, GradeWords>> = {
  1: { label: "Machine only", description: "A machine labels it. No one checks." },
  2: {
    label: "Machine, then a person checks",
    description: "A machine labels it, then a person checks and corrects the labels.",
  },
  3: {
    label: "A person first",
    description: "A person labels it. A machine labels it too and flags where they differ.",
  },
  4: { label: "A person only", description: "A person labels it. No machine." },
};

export const GRADES: readonly Exclude<AnnotationGrade, 0>[] = [1, 2, 3, 4];

export interface Tier {
  readonly name: string;
  readonly description: string;
}

/** The machine labelling tiers the quote prices, cheapest first as the table lists them. */
export const TIERS: readonly Tier[] = Object.entries(table.tiers).map(([name, tier]) => ({
  name,
  description: tier.description,
}));

/** A tier's name as a person reads it. */
export function tierName(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export interface ProductOffer {
  /** The product's name in the price table: `LabelProduct`'s name, lower case. */
  readonly name: string;
  readonly description: string;
  /** Free: the task, made for every request, and the outcome and quality, made with a
   * reading subtasks pay for. */
  readonly free: boolean;
}

/** Every product the price table names, in its order. */
export const PRODUCTS: readonly ProductOffer[] = Object.entries(table.products).map(
  ([name, product]) => ({ name, description: product.description, free: product.free }),
);

/** The products a person can ask for at a tier: those it prices for some rig, and the free
 * ones but the task, which every request makes. */
export function offeredProducts(tier: string): readonly ProductOffer[] {
  const priced = new Set(table.measured.filter((row) => row.tier === tier).map((row) => row.product));
  return PRODUCTS.filter((product) =>
    product.free ? product.name !== "task" : priced.has(product.name),
  );
}

/** A product's name as a person reads it. */
export function productName(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Every product but unspecified: `LabelProduct`'s numbers, which name the price table's
 * products in its order (`episodes-search.test.ts` holds the two together). */
export const LABEL_PRODUCTS: readonly LabelProduct[] = [1, 2, 3, 4, 5];

/** A product of the proto as a person reads it. */
export function labelProductName(value: LabelProduct): string {
  const offer = PRODUCTS[value - 1];
  return offer === undefined ? "Unknown" : productName(offer.name);
}
